import type { ChannelKind } from '@apm/contracts';

/**
 * 홈쇼핑 채널의 **시작점 기억** (v26, `docs/53` §2 · 절대 규칙 70).
 *
 * ── 무엇을 기억하는가 ────────────────────────────────────────
 *
 * **초가 아니라 항목 하나다.** 「보던 상품을 처음부터 다시」가 홈쇼핑에서 옳은 이유는
 * 상품 영상 한 편이 곧 한 상품의 소개라, 중간부터 트는 것은 그 상품을 반만 보여주는
 * 일이기 때문이다 (`docs/38` D5 가 홈쇼핑의 이어보기를 막은 그 판단을 뒤집지 않는다).
 *
 * ── 규칙 46 과 부딪히지 않는다 ───────────────────────────────
 *
 * 규칙 46 이 계정 축으로 좁힌 것은 `WatchProgress` — **「몇 초부터」**다. 이 값은 초가
 * 없고, 서버에 가지 않고, `WatchProgress` 를 한 행도 만들지 않으며, **홈쇼핑에만 있다.**
 * 홈쇼핑은 편성이 모두에게 같아 순서만 도는 이 값이 사람을 가리지 않는다.
 *
 * ── 순수 함수만 둔다 ─────────────────────────────────────────
 *
 * 저장소를 만지는 것은 `channels.ts`(`bloom.channelId` 바로 옆)이고 여기는 그것이 준
 * 객체를 받기만 한다 — `localStorage` 를 직접 만지면 아래 판정들이 **node 테스트에서
 * 통째로 빠진다** (`epg.ts`·`channel-selection.ts` 가 순수로 떨어져 나온 그 이유).
 */

/** 망가진 값이 무한히 자라지 않게. 채널이 이만큼 많아지면 그때 다시 본다 */
const LAST_ITEMS_MAX = 32;

export type LastItems = Readonly<Record<string, string>>;

/**
 * 저장소에서 온 `unknown` 하나에서 (채널 → 항목) 표를 낸다. **여기가 유일한 검증 자리다.**
 *
 * 읽을 때마다 모양을 다시 본다 — 저장한 것은 우리지만 그 사이에 판이 바뀌었을 수 있고,
 * **어긋난 것을 그대로 넘기면 진입에서 없는 상품을 가리킨다.** 그때 서버는 회전만 안 하고
 * 넘어가지만(아래 참조), 값이 문자열이 아니면 URL 조립에서 터진다.
 *
 * ★ **「공개 목록에 없는 채널은 버린다」를 넣지 않는다** — `normalizeFavorites` 와 같은
 * 방침이다. 편성이 잠깐 빈 채널은 `/v1/channels` 에서 빠지고(절대 규칙 1 의 승계),
 * 그날 앱을 한 번 열면 **기억이 조용히 사라진다.** 표시가 거르고 저장이 기억한다.
 */
export function normalizeLastItems(raw: unknown): LastItems {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [channelId, productId] of Object.entries(raw as Record<string, unknown>)) {
    if (channelId === '' || typeof productId !== 'string' || productId === '') continue;
    out[channelId] = productId;
    if (Object.keys(out).length >= LAST_ITEMS_MAX) break;
  }
  return out;
}

/**
 * 한 칸을 갱신한 새 표. **상한을 넘으면 가장 오래 전에 넣은 칸을 버린다** —
 * 객체의 키 순서가 곧 넣은 순서다.
 */
export function withLastItem(current: LastItems, channelId: string, productId: string): LastItems {
  if (channelId === '' || productId === '') return current;
  // 같은 값이면 그대로 돌려준다 — 쓰는 쪽이 저장소를 건드리지 않게 하는 신호다.
  if (current[channelId] === productId) return current;
  const next: Record<string, string> = { ...current };
  delete next[channelId];
  next[channelId] = productId;
  const keys = Object.keys(next);
  for (const stale of keys.slice(0, Math.max(0, keys.length - LAST_ITEMS_MAX))) {
    delete next[stale];
  }
  return next;
}

/**
 * ★★ **자물쇠 ②** (절대 규칙 70). 이 채널에 들어갈 때 어느 항목부터 틀 것인가.
 *
 * **홈쇼핑이 아니면 언제나 `null`** 이다. 일반 채널에 값을 주면 `start({ from })` 이
 * 서버의 `resolveResume` 에 「고른 것」으로 읽혀 **`positionSec: 0`** 이 되고, 첫 주기
 * 보고가 그 0 으로 **저장된 이어보기 지점을 덮는다 — 되돌릴 방법이 없다.**
 *
 * 자물쇠 ①(쓰기가 홈쇼핑에서만 일어난다)이 이미 있는데도 여기를 두는 이유는 둘이다.
 * **하나** — 하나면 그 하나를 지운 커밋이 조용히 통과한다. **둘** — 이쪽이 순수 함수라
 * **node 테스트가 규칙을 직접 잰다.** 저장소를 만지는 쪽에만 두면 그 판정이 검사 밖이다.
 *
 * `kind` 가 `null` 일 때도 `null` 이다 — 채널 목록을 아직 못 받은 상태이고, 그때
 * 홈쇼핑으로 가정하면 **일반 채널에 값이 갈 수 있는 유일한 경로**가 열린다.
 */
export function entryStartFor(
  kind: ChannelKind | null | undefined,
  items: LastItems,
  channelId: string,
): string | null {
  if (kind !== 'homeshopping') return null;
  const productId = items[channelId];
  return productId === undefined || productId === '' ? null : productId;
}
